/*
 * Copyright 2021 The Backstage Authors
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */

import { default as React } from 'react';
import { useEffect } from 'react';
import { useSearch } from '@backstage/plugin-search-react';
import Tab from '@material-ui/core/Tab';
import Tabs from '@material-ui/core/Tabs';
import { makeStyles } from '@material-ui/core/styles';
import { Theme } from '@material-ui/core/styles';

const useStyles = makeStyles((theme: Theme) => ({
  tabs: {
    borderBottom: `1px solid ${theme.palette.textVerySubtle}`,
  },
  tab: {
    height: '50px',
    color: `${theme.palette.text.primary}`,
    minWidth: '130px',
    fontSize: theme.typography.pxToRem(14),
    fontWeight: theme.typography.fontWeightBold as any,
  },
  inactiveTab: {
    color: theme.palette.link,
  },
}));

/**
 * @public
 */
export type SearchTypeTabsProps = {
  types: Array<{
    value: string;
    name: string;
  }>;
  defaultValue?: string;
};

export const SearchTypeTabs = (props: SearchTypeTabsProps) => {
  const classes = useStyles();
  const { setPageCursor, setTypes, types, term } = useSearch();
  const { defaultValue, types: givenTypes } = props;
  const [activeTab, setActiveTab] = React.useState(() => {
    // Check URL for existing types parameter on initialization
    const urlParams = new URLSearchParams(window.location.search);
    const urlTypes = urlParams.get('types[]');
    if (urlTypes) {
      return urlTypes;
    }

    return types.length === 0 ? 'handbook' : types[0];
  });

  const changeTab = (_: React.ChangeEvent<{}>, newType: string) => {
    setTypes(newType !== '' ? [newType] : []);
    setActiveTab(newType);
    setPageCursor(undefined);
  };

  // Handle any provided defaultValue
  useEffect(() => {
    if (defaultValue) {
      // Check if URL already has types parameter
      const urlParams = new URLSearchParams(window.location.search);
      const hasTypesParam = urlParams.has('types[]') || urlParams.has('types');

      // Only set defaultValue if there are no existing types in URL AND no query param
      // AND no term in search context (to avoid overriding during navigation)
      if (!hasTypesParam && !term) {
        const timeoutId = setTimeout(() => {
          // Re-check URL parameters after timeout to ensure they haven't changed
          const updatedUrlParams = new URLSearchParams(window.location.search);
          const stillHasNoTypesParam =
            !updatedUrlParams.has('types[]') && !updatedUrlParams.has('types');

          // Only set default if URL still has no types parameter AND no term
          if (stillHasNoTypesParam && !term && types.length === 0) {
            setTypes([defaultValue]);
          }
        }, 100);

        return () => clearTimeout(timeoutId);
      }
    }
    return undefined;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const definedTypes = [
    ...givenTypes,
    {
      value: 'all',
      name: 'All',
    },
  ];

  return (
    <Tabs
      aria-label="List of search types tabs"
      className={classes.tabs}
      indicatorColor="primary"
      variant="scrollable"
      scrollButtons="auto"
      value={types.length === 0 ? '' : types[0]}
      onChange={changeTab}
    >
      {definedTypes.map((type, idx) => (
        <Tab
          key={idx}
          className={`${classes.tab} ${
            activeTab !== type.value ? classes.inactiveTab : ''
          }`}
          label={type.name}
          value={type.value}
        />
      ))}
    </Tabs>
  );
};
